import { memo, useMemo, useState } from 'react';
import classNames from 'classnames';
import CaseStage from './CaseStage/CaseStage';
import { AsyncDataHandler, Button, Tooltip } from '@ds';
import { ButtonSize, ButtonStyle, ButtonVariant } from '@ds/elements/Button/Button.types';
import { useScreenSize } from '@ds/utils/hooks/useScreenSize';
import { useGetCaseHighLevelStatus } from 'src/shared/queries/useGetCaseHighLevelStatus';
import { useGetCaseQuestionnaires } from 'src/shared/queries/useGetCaseQuestionnaires';
import { useGetCaseSubprojects } from 'src/shared/queries/useGetCaseSubprojects';
import SupplementalFilingsModal from 'src/components/SupplementalFilingsModal/SupplementalFilingsModal';
import { ICaseInfo, ICaseStage, Stage_Status } from 'src/shared/types/Case';
import { CountryCode, GATHERING_DOCS_STAGE_NAME } from 'src/utils/constants/common';
import { CaseDocumentsTabs } from 'src/utils/constants/Case';
import { useNavigateWithSecureParams } from 'src/utils/hooks/useNavigateWithSecureParams';
import { useThemeStore } from 'src/stores/ThemeStore';
import RemoteCaseDetailsBanner from './RemoteCaseDetailsBanner/RemoteCaseDetailsBanner';
import { useAidiAccess } from 'src/utils/hooks/useAidiAccess';
import styles from './CaseTimeline.module.scss';

const getCurrentStageIndex = (stages?: ICaseStage[] | null): number => {
  if (!stages || stages.length === 0) return -1;

  let index = stages.length - 1;
  for (let i = stages.length - 1; i >= 0; i--) {
    if (stages[i].status === Stage_Status.NOT_STARTED || stages[i].status === Stage_Status.STARTED) {
      index = i;
    }
  }

  return index;
};

const getCurrentStage = (stages?: ICaseStage[] | null): ICaseStage | null => {
  const currentStageIndex = getCurrentStageIndex(stages);

  if (currentStageIndex < 0 || !stages) return null;

  return stages[currentStageIndex] ?? null;
};

const RemoteCaseTimeline = (props: { stages: ICaseStage[] }) => {
  const { stages } = props;

  const currentStage = getCurrentStage(stages);

  if (!currentStage) return null;

  return (
    <div className={styles['remote-case-timeline']}>
      <CaseStage stage={currentStage} isRemote={true} />
    </div>
  );
};

const CaseOptions = memo(
  (props: { caseDetails: ICaseInfo; isGatheringState?: boolean; isPastGatheringState?: boolean }) => {
    const { caseDetails, isGatheringState = false, isPastGatheringState = false } = props;
    const { navigate } = useNavigateWithSecureParams();
    const { caseId } = caseDetails || { caseId: 0 };
    const { data: questionnaires, isLoading: isQuestionnairesLoading } = useGetCaseQuestionnaires(caseId);
    const { data: subprojects, isLoading: isSubprojectsLoading } = useGetCaseSubprojects(caseId);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const loaderSize = { width: '120px', height: '20px' };

    const questionnairesState = useMemo(() => {
      const isEmpty = !isQuestionnairesLoading && questionnaires?.length === 0;
      const isAllCompleted = (questionnaires?.length ?? 0) > 0 && !questionnaires?.find((q) => !q.isComplete);

      return { isEmpty, isAllCompleted };
    }, [questionnaires, isQuestionnairesLoading]);

    const isDisabledSubprojects = !subprojects || subprojects.length === 0;
    const isDisabledQuestionnaires = !questionnaires || questionnaires.length === 0;
    const { shouldDisableLegacy: hideLegacyQuestionaireOrDocuments, loading: loadingAidiAccess } = useAidiAccess({
      caseId: caseDetails?.caseId || null,
      flagOnly: true,
    });

    return (
      <div
        className={classNames(styles['case-options'], {
          [styles['case-options-gathering']]: isGatheringState || isPastGatheringState,
        })}
      >
        {!loadingAidiAccess && !hideLegacyQuestionaireOrDocuments && (
          <Button
            className={classNames(styles['case-option'], {
              [styles['case-option-highlighted']]: isPastGatheringState,
            })}
            variant={
              isPastGatheringState
                ? ButtonVariant.contained
                : isGatheringState
                  ? ButtonVariant.outlined
                  : ButtonVariant.text
            }
            styleType={ButtonStyle.secondary}
            size={ButtonSize.small}
            onClick={() =>
              navigate(`/case-documents`, {
                secureParams: {
                  caseId,
                  tab: CaseDocumentsTabs.CaseDocuments,
                },
              })
            }
          >
            <div className={classNames(styles['option-icon'], styles['documents-icon'])}></div>
            <p>
              {isPastGatheringState ? 'add more documents' : isGatheringState ? 'upload documents' : 'case documents'}
            </p>
          </Button>
        )}
        <AsyncDataHandler isLoading={isQuestionnairesLoading || loadingAidiAccess} loader={loaderSize}>
          {!hideLegacyQuestionaireOrDocuments && (
            <span className={classNames(styles['case-option-wrapper'], 'tooltip-no-questionnaires')}>
              <Button
                className={styles['case-option']}
                variant={isGatheringState || isPastGatheringState ? ButtonVariant.outlined : ButtonVariant.text}
                styleType={ButtonStyle.secondary}
                size={ButtonSize.small}
                disabled={isDisabledQuestionnaires}
                onClick={() => {
                  if (!isDisabledQuestionnaires)
                    navigate(`/case-documents?tab=${CaseDocumentsTabs.Questionnaire}`, {
                      secureParams: {
                        caseId,
                        tab: CaseDocumentsTabs.Questionnaire,
                      },
                    });
                }}
              >
                {questionnairesState.isEmpty && (
                  <Tooltip
                    target=".tooltip-no-questionnaires"
                    mouseTrack
                    mouseTrackLeft={10}
                    content="There are no questionnaires assigned to this case"
                  ></Tooltip>
                )}
                <div className="relative">
                  {questionnairesState.isAllCompleted && <div className={styles['completed-questionnaies-icon']}></div>}
                  <div className={classNames(styles['option-icon'], styles['questionnaies-icon'])}></div>
                </div>
                <p className={styles['questionnaires-title']}>questionnaires</p>
              </Button>
            </span>
          )}
        </AsyncDataHandler>

        <AsyncDataHandler isLoading={isSubprojectsLoading} loader={loaderSize}>
          <span className={classNames(styles['case-option-wrapper'], 'tooltip-no-subprojects')}>
            <Button
              variant={
                !hideLegacyQuestionaireOrDocuments && (isGatheringState || isPastGatheringState)
                  ? ButtonVariant.outlined
                  : ButtonVariant.text
              }
              styleType={ButtonStyle.secondary}
              size={ButtonSize.small}
              disabled={isDisabledSubprojects}
              className={styles['case-option']}
              onClick={() => {
                if (!isDisabledSubprojects) setIsModalVisible(true);
              }}
            >
              {!isSubprojectsLoading && subprojects?.length === 0 && (
                <Tooltip
                  target=".tooltip-no-subprojects"
                  mouseTrack
                  mouseTrackLeft={10}
                  content="There are no supplemental filings for this case"
                ></Tooltip>
              )}
              <div className={classNames(styles['option-icon'], styles['grid-icon'])}></div>
              <p className={styles['questionnaires-title']}>Supplemental Filings</p>
            </Button>
          </span>
        </AsyncDataHandler>
        <SupplementalFilingsModal
          visible={isModalVisible}
          caseName={caseDetails?.caseName ?? ''}
          subprojects={subprojects ?? []}
          onModalClosed={() => setIsModalVisible(false)}
        />
      </div>
    );
  }
);

const CaseTimeline = (props: { caseDetails: ICaseInfo }) => {
  const { caseDetails } = props;
  const isRemote = caseDetails.isRemote ?? false;
  const { isMobile } = useScreenSize();
  const { isDeloitteTheme } = useThemeStore();
  const caseId = caseDetails?.caseId ?? 0;
  const {
    data: stages,
    isLoading: isLoadingHighLevelStatus,
    error: highLevelStatusError,
  } = useGetCaseHighLevelStatus(caseId);
  const currentStageIndex = getCurrentStageIndex(stages);
  const gatheringStageIndex = stages?.findIndex((stage) => stage.name === GATHERING_DOCS_STAGE_NAME) ?? -1;
  const currentStage = getCurrentStage(stages);
  const isGatheringState = currentStage?.name === GATHERING_DOCS_STAGE_NAME;
  const isPastGatheringState = gatheringStageIndex >= 0 && currentStageIndex > gatheringStageIndex;

  return (
    <AsyncDataHandler
      className={isLoadingHighLevelStatus ? 'mt-6' : ''}
      isLoading={isLoadingHighLevelStatus}
      error={highLevelStatusError}
    >
      <div className={styles['case-timeline-container']}>
        <div className={classNames(styles['case-timeline-header'], { 'mb-4': isRemote })}>
          <div className={styles['case-timeline-title-group']}>
            <h4>Case {!isRemote ? 'timeline' : 'status'}</h4>
            <div className={styles['case-matter-number']}>
              <p>{caseDetails?.balNumber}</p>
              <Tooltip
                mouseTrack
                target={`.${styles['matter-number-info-icon']}`}
                position={isMobile ? 'left' : 'right'}
                content="Matter number is a unique identifier for each case"
              />
              <div
                className={classNames(styles['matter-number-info-icon'], {
                  [styles['matter-number-info-icon-deloitte']]: isDeloitteTheme,
                })}
              ></div>
            </div>
          </div>
          {caseDetails.isActive && !isRemote && (
            <CaseOptions
              caseDetails={caseDetails}
              isGatheringState={isGatheringState}
              isPastGatheringState={isPastGatheringState}
            />
          )}
        </div>

        <div className={styles['case-stages-container']}>
          {stages != null && stages.length === 0 ? (
            isRemote ? null : (
              <div className={styles['no-stages-container']}>
                <p>There is no timeline for this case</p>
              </div>
            )
          ) : (
            <div className={styles['case-stages-details']}>
              {isRemote ? (
                stages ? (
                  <RemoteCaseTimeline stages={stages} />
                ) : null
              ) : (
                stages?.map((stage) => {
                  return (
                    <CaseStage
                      key={stage.name}
                      stage={stage}
                      isUSCountry={caseDetails.caseCountryCode === CountryCode.UnitedStates}
                      caseDetails={caseDetails}
                    />
                  );
                })
              )}
            </div>
          )}
        </div>

        {isRemote && (
          <RemoteCaseDetailsBanner
            caseName={caseDetails?.caseName ?? ''}
            universalCaseId={caseDetails?.universalCaseId || ''}
          />
        )}
      </div>
    </AsyncDataHandler>
  );
};

export default memo(CaseTimeline);
